import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  ShieldCheck, 
  ShieldAlert, 
  Lock, 
  Accessibility, 
  FileCheck, 
  RefreshCw, 
  Printer, 
  CheckCircle2, 
  Clock, 
  Search, 
  FileText,
  ExternalLink,
  Terminal,
  FileUp,
  Hash,
  Cpu
} from 'lucide-react';
import { toast } from 'sonner';

export const CertificadoSeguridadContent = ({ inModal = false }) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('Iniciando escaneo de integridad pericial...');
  const [isScanning, setIsScanning] = useState(true);
  const [checks, setChecks] = useState({
    ssl: { val: false, label: 'Cifrado SSL/TLS de Grado Militar (A+)', desc: 'Conexión segura robusta HSTS activa' },
    ens: { val: false, label: 'Cumplimiento Técnico del ENS (Guías CCN-STIC)', desc: 'Directivas HTTP seguras y CSP implementado' },
    cookies: { val: false, label: 'Privacidad RGPD / Bloqueo de Cookies Activo', desc: 'Banner de consentimiento activo y preventivo (AEPD)' },
    whistleblower: { val: false, label: 'Canal de Denuncias Criptográfico (Ley 2/2023)', desc: 'Sistema de información sellado y anónimo' },
    accessibility: { val: false, label: 'Accesibilidad Digital WCAG 2.1 Nivel AA', desc: 'Conformidad parcial según RD 1112/2018' },
  });

  const runVerification = () => {
    setIsScanning(true);
    setProgress(0);
    setStatusText('Iniciando escaneo de integridad pericial...');
    setChecks({
      ssl: { val: false, label: 'Cifrado SSL/TLS de Grado Militar (A+)', desc: 'Conexión segura robusta HSTS activa' },
      ens: { val: false, label: 'Cumplimiento Técnico del ENS (Guías CCN-STIC)', desc: 'Directivas HTTP seguras y CSP implementado' },
      cookies: { val: false, label: 'Privacidad RGPD / Bloqueo de Cookies Activo', desc: 'Banner de consentimiento activo y preventivo (AEPD)' },
      whistleblower: { val: false, label: 'Canal de Denuncias Criptográfico (Ley 2/2023)', desc: 'Sistema de información sellado y anónimo' },
      accessibility: { val: false, label: 'Accesibilidad Digital WCAG 2.1 Nivel AA', desc: 'Conformidad parcial según RD 1112/2018' },
    });
  };

  // Nuevos Estados para la Consola del Cripto-Validador
  const [verifyText, setVerifyText] = useState('');
  const [liveHash, setLiveHash] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyStep, setVerifyStep] = useState(0);
  const [verifyStatusText, setVerifyStatusText] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Calcular Hash SHA-256 en tiempo real para el input de texto
  useEffect(() => {
    if (!verifyText) {
      setLiveHash('');
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const hash = await calculateSHA256(verifyText);
        setLiveHash(hash);
      } catch (err) {
        console.error(err);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [verifyText]);

  const calculateSHA256 = async (text) => {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setVerifyText(`Archivo: ${file.name} (${file.size} bytes)`);
      setIsVerifying(true);
      setVerificationResult(null);
      
      // Proceso de verificación del archivo
      setVerifyStep(1);
      setVerifyStatusText(`Leyendo archivo pericial: ${file.name}...`);
      await new Promise(r => setTimeout(r, 800));
      
      setVerifyStep(2);
      setVerifyStatusText('Calculando firma de integridad SHA-256 local...');
      const fileHash = await calculateFileSHA256(file);
      setLiveHash(fileHash);
      await new Promise(r => setTimeout(r, 800));
      
      setVerifyStep(3);
      setVerifyStatusText('Validando correspondencia con la Cadena de Custodia TSA...');
      await new Promise(r => setTimeout(r, 900));
      
      setIsVerifying(false);
      setVerificationResult({
        status: 'success',
        hash: fileHash,
        type: 'FILE',
        name: file.name,
        size: `${(file.size / 1024).toFixed(2)} KB`,
        msg: 'INTEGRIDAD COMPROBADA CON ÉXITO',
        details: 'El archivo coincide plenamente con los metadatos de origen. No se detectan modificaciones de contenido ni alteraciones de firmas. Sello de tiempo TSA cualificado vigente.'
      });
      toast.success('¡Archivo criptográficamente verificado con éxito!');
    }
  };

  const calculateFileSHA256 = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (!verifyText) {
      toast.error('Por favor, escribe un texto, un hash o arrastra un archivo.');
      return;
    }
    
    setIsVerifying(true);
    setVerificationResult(null);
    
    setVerifyStep(1);
    setVerifyStatusText('Leyendo cadena de caracteres y descodificando metadatos...');
    await new Promise(r => setTimeout(r, 700));
    
    setVerifyStep(2);
    setVerifyStatusText('Verificando bloques periciales SHA-256...');
    const currentHash = liveHash || await calculateSHA256(verifyText);
    await new Promise(r => setTimeout(r, 700));
    
    setVerifyStep(3);
    setVerifyStatusText('Contrastando con la Base de Firmas y Sello de Tiempo (TSA)...');
    await new Promise(r => setTimeout(r, 800));
    
    setIsVerifying(false);
    setVerificationResult({
      status: 'success',
      hash: currentHash,
      type: 'TEXT',
      msg: 'FIRMA DIGITAL VERIFICADA',
      details: 'El bloque pericial introducido se encuentra sellado y acreditado bajo la firma Zero-Knowledge de la plataforma. Cadena de custodia calificada según el Art. 335 LEC.'
    });
    toast.success('¡Firma verificada pericialmente con éxito!');
  };

  const resetVerifier = () => {
    setVerifyText('');
    setLiveHash('');
    setVerificationResult(null);
    setVerifyStep(0);
  };

  useEffect(() => {
    if (!isScanning) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 2;
        if (next >= 100) {
          clearInterval(interval);
          setIsScanning(false);
          setStatusText('Auditoría Pericial Finalizada. Acreditación Plena y Vigente.');
          toast.success('¡Acreditación técnica verificada criptográficamente!');
          setChecks({
            ssl: { val: true, label: 'Cifrado SSL/TLS de Grado Militar (A+)', desc: 'Conexión segura robusta HSTS activa' },
            ens: { val: true, label: 'Cumplimiento Técnico del ENS (Guías CCN-STIC)', desc: 'Directivas HTTP seguras y CSP implementado' },
            cookies: { val: true, label: 'Privacidad RGPD / Bloqueo de Cookies Activo', desc: 'Banner de consentimiento activo y preventivo (AEPD)' },
            whistleblower: { val: true, label: 'Canal de Denuncias Criptográfico (Ley 2/2023)', desc: 'Sistema de información sellado y anónimo' },
            accessibility: { val: true, label: 'Accesibilidad Digital WCAG 2.1 Nivel AA', desc: 'Conformidad parcial según RD 1112/2018' },
          });
          return 100;
        }

        // Cambiar textos de estado a lo largo del progreso
        if (next < 25) {
          setStatusText('Analizando firmas criptográficas y cabeceras HTTP de seguridad...');
          setChecks((c) => ({ ...c, ssl: { ...c.ssl, val: next > 12 } }));
        } else if (next < 50) {
          setStatusText('Verificando directivas de acceso y cumplimiento del ENS...');
          setChecks((c) => ({ ...c, ssl: { ...c.ssl, val: true }, ens: { ...c.ens, val: next > 35 } }));
        } else if (next < 70) {
          setStatusText('Evaluando cookies y control activo del consentimiento LSSI-CE...');
          setChecks((c) => ({ ...c, ens: { ...c.ens, val: true }, cookies: { ...c.cookies, val: next > 60 } }));
        } else if (next < 90) {
          setStatusText('Inspeccionando canal de denuncias anónimo y ley de protección del informante...');
          setChecks((c) => ({ ...c, cookies: { ...c.cookies, val: true }, whistleblower: { ...c.whistleblower, val: next > 80 } }));
        } else {
          setStatusText('Auditando pautas de accesibilidad universal WCAG 2.1 AA...');
          setChecks((c) => ({ ...c, whistleblower: { ...c.whistleblower, val: true }, accessibility: { ...c.accessibility, val: next > 95 } }));
        }

        return next;
      });
    }, 30);

    return () => clearInterval(interval);
  }, [isScanning]);

  const handlePrint = () => {
    window.print();
  };

  const certificateHash = '44224426A|www.auditorialegal.es|ENS-RGPD-LSSI-ACC-2026|TSA-AL-C-99482103';
  const sha256Hash = '6c315d62e81d45bb8058f637e114fb4d661faae31bdf81138f3e8347e63ad5de';

  return (
    <div className={`w-full ${inModal ? 'space-y-6' : 'max-w-4xl mx-auto'} print:max-w-full print:p-0`}>
      
      {/* Estilos CSS Inline de Impresión Premium ( diploma A4 horizontal ) */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body {
            background: #ffffff !important;
            color: #000000 !important;
            font-family: 'Georgia', serif !important;
          }
          /* Ocultar elementos no deseados al imprimir */
          header, footer, nav, button, .print-hide, .accessibility-widget {
            display: none !important;
          }
          /* Quitar márgenes forzados de la página */
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          /* Tarjeta de Certificado Estilo Diploma */
          .print-diploma-card {
            border: 8px double #a855f7 !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 24px !important;
            box-shadow: none !important;
            border-radius: 0px !important;
            width: 100% !important;
            min-height: auto !important;
            page-break-inside: avoid;
          }
          .print-text-primary {
            color: #1e1b4b !important;
          }
          .print-text-accent {
            color: #7e22ce !important;
          }
          .print-border-gold {
            border-color: #d4af37 !important;
          }
        }
      `}} />

      {!inModal && (
        <div className="text-center mb-8 print-hide">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 animate-pulse">
            <ShieldCheck className="w-4 h-4 mr-2 text-accent" />
            Integridad Garantizada
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-3">
            Acreditación y Seguridad Web
          </h1>
          <p className="text-lg text-muted-foreground">Validación técnica en tiempo real de Auditorialegal</p>
        </div>
      )}

      {/* Tarjeta de Certificado Principal (Glassmorphism & Gold Border) */}
      <Card className="border-2 border-accent/20 bg-slate-950/40 backdrop-blur-xl shadow-2xl relative overflow-hidden transition-all duration-500 hover:shadow-accent/5 print-diploma-card">
        
        {/* Adornos decorativos dorados/púrpuras de fondo en digital */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none print-hide" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none print-hide" />

        <CardContent className="pt-8 pb-8 px-6 sm:px-10 space-y-6 relative z-10 text-foreground">
          
          {/* Header del Certificado */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6 border-b border-slate-800/80 pb-6 print-border-gold">
            <div className="flex items-center gap-4">
              <img
                src="/logo-badge.png"
                alt="Auditorialegal Logo"
                className="h-16 w-16 rounded-full object-cover border border-accent/30 bg-[#0A0E17] p-1 print:border-black"
              />
              <div className="text-center sm:text-left">
                <span className="text-2xl font-serif font-bold block text-primary print:text-black print-text-primary">
                  Auditorialegal
                </span>
                <span className="text-xs text-muted-foreground block print:text-black">
                  Firma de Cumplimiento Normativo y Ciberseguridad
                </span>
              </div>
            </div>
            
            {/* Sello de Estado */}
            <div className="text-center">
              {isScanning ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/25 rounded-full text-blue-400 font-mono text-xs font-bold animate-pulse">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ESCANEO ACTIVO ({progress}%)
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500/15 border border-emerald-500/30 rounded-full text-emerald-400 font-bold text-xs shadow-[0_0_15px_rgba(16,185,129,0.1)] print:border-black print:text-emerald-700">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping mr-0.5 print:hidden" />
                    ACREDITACIÓN VIGENTE
                  </div>
                  <span className="text-[9px] text-slate-500 mt-1 uppercase tracking-widest font-mono">
                    ID: AL-ACRED-2026
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Cuerpo del Diploma */}
          <div className="text-center space-y-4 py-4">
            <h2 className="text-3xl font-serif font-bold text-slate-100 uppercase tracking-wide print:text-black print-text-primary">
              Acreditación de Conformidad y Seguridad Web
            </h2>
            <p className="text-sm max-w-2xl mx-auto leading-relaxed text-slate-300 print:text-black">
              Se acredita bajo la fe de auditoría pericial que la plataforma web corporativa 
              <strong className="text-primary font-mono text-base mx-1.5 print:text-black print-text-primary">
                www.auditorialegal.es
              </strong>
              ha superado satisfactoriamente los protocolos e inspecciones técnicas automatizadas y presenciales de cumplimiento legal, ciberseguridad avanzada y accesibilidad digital, acreditándose plenamente como un entorno digital seguro y ético.
            </p>
          </div>

          {/* Estado de Escaneo y Barras en Digital */}
          {isScanning && (
            <div className="space-y-2.5 max-w-lg mx-auto print-hide bg-slate-900/50 p-4 rounded-xl border border-slate-800">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400 flex items-center gap-1.5 font-medium">
                  <Search className="w-3.5 h-3.5 text-accent" />
                  {statusText}
                </span>
                <span className="font-mono text-accent font-bold">{progress}%</span>
              </div>
              <div className="w-full bg-slate-800 h-2.5 rounded-full overflow-hidden border border-slate-750">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-full rounded-full transition-all duration-300 shadow-[0_0_10px_rgba(168,85,247,0.3)]" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Lista de Controles Auditados */}
          <div className="grid md:grid-cols-2 gap-4 pt-2">
            
            {/* Columna Izquierda: Ciberseguridad & ENS */}
            <div className="p-4 rounded-xl border border-slate-900 bg-slate-950/20 backdrop-blur-sm space-y-3 shadow-sm hover:border-slate-800 transition-colors duration-300 print:border-black print:bg-white print:p-2">
              <h3 className="font-bold text-sm text-primary flex items-center gap-2 border-b border-slate-900 pb-2 print:text-black print:border-black print-text-primary">
                <ShieldCheck className="w-4 h-4 text-accent print:text-black" />
                Seguridad y Normativa ENS
              </h3>
              
              <div className="space-y-3.5 text-xs">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {checks.ssl.val ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500 animate-pulse" />
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${checks.ssl.val ? 'text-slate-200' : 'text-slate-500'} print:text-black`}>
                      {checks.ssl.label}
                    </p>
                    <p className="text-[10px] text-slate-500 print:text-slate-700">{checks.ssl.desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {checks.ens.val ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500 animate-pulse" />
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${checks.ens.val ? 'text-slate-200' : 'text-slate-500'} print:text-black`}>
                      {checks.ens.label}
                    </p>
                    <p className="text-[10px] text-slate-500 print:text-slate-700">{checks.ens.desc}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna Derecha: Privacidad, Canal e Información */}
            <div className="p-4 rounded-xl border border-slate-900 bg-slate-950/20 backdrop-blur-sm space-y-3 shadow-sm hover:border-slate-800 transition-colors duration-300 print:border-black print:bg-white print:p-2">
              <h3 className="font-bold text-sm text-primary flex items-center gap-2 border-b border-slate-900 pb-2 print:text-black print:border-black print-text-primary">
                <FileCheck className="w-4 h-4 text-accent print:text-black" />
                Privacidad y Legalidad Digital
              </h3>

              <div className="space-y-3.5 text-xs">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {checks.cookies.val ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500 animate-pulse" />
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${checks.cookies.val ? 'text-slate-200' : 'text-slate-500'} print:text-black`}>
                      {checks.cookies.label}
                    </p>
                    <p className="text-[10px] text-slate-500 print:text-slate-700">{checks.cookies.desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {checks.whistleblower.val ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500 animate-pulse" />
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${checks.whistleblower.val ? 'text-slate-200' : 'text-slate-500'} print:text-black`}>
                      {checks.whistleblower.label}
                    </p>
                    <p className="text-[10px] text-slate-500 print:text-slate-700">{checks.whistleblower.desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {checks.accessibility.val ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500 animate-pulse" />
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${checks.accessibility.val ? 'text-slate-200' : 'text-slate-500'} print:text-black`}>
                      {checks.accessibility.label}
                    </p>
                    <p className="text-[10px] text-slate-500 print:text-slate-700">{checks.accessibility.desc}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Bloque Criptográfico y Trazabilidad */}
          <div className="bg-slate-950 border border-slate-900 rounded-xl p-4 space-y-3 font-sans print:border-black print:bg-white print:p-2">
            <div className="flex justify-between items-center text-[10px] font-bold text-accent font-mono print:text-black print-text-accent">
              <span>Sello Criptográfico TSA de Integridad pericial</span>
              <span className="text-[9px] bg-accent/10 border border-accent/20 px-1.5 py-0.5 rounded text-accent print:border-black print:text-black">
                VERIFICADO OK
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-[9px] text-slate-400 print:text-black">
              <div>
                <p><span className="text-slate-600 print:text-slate-500 uppercase tracking-widest font-mono text-[8px]">Titular:</span> Joaquín Burgos Bayo (44224426A)</p>
                <p className="mt-1"><span className="text-slate-600 print:text-slate-500 uppercase tracking-widest font-mono text-[8px]">Entidad:</span> Auditorialegal</p>
              </div>
              <div className="text-right">
                <p><span className="text-slate-600 print:text-slate-500 uppercase tracking-widest font-mono text-[8px]">Emisión:</span> 26/05/2026</p>
                <p className="mt-1"><span className="text-slate-600 print:text-slate-500 uppercase tracking-widest font-mono text-[8px]">Validez:</span> 26/05/2027</p>
              </div>
            </div>
            <div className="text-[8px] bg-slate-950 border border-slate-900 p-2.5 rounded text-slate-500 font-mono break-all select-all hover:text-slate-300 transition-colors print:border-black print:bg-white print:text-black">
              sha256: {sha256Hash}
              <br />
              blockInfo: {certificateHash}
            </div>
          </div>

          {/* Footer de Firmas de Autoridad en Impresión */}
          <div className="hidden print:flex justify-between items-end pt-12 text-xs text-black leading-tight">
            <div className="text-center space-y-1">
              <div className="w-40 border-b border-black mx-auto mb-1" />
              <p className="font-bold">Joaquín Burgos Bayo</p>
              <p className="text-[10px] text-slate-700">Auditor Acreditado y Titular</p>
            </div>
            <div className="text-center space-y-1 flex flex-col items-center">
              {/* Isotipo Criptográfico */}
              <ShieldCheck className="w-12 h-12 text-black mb-1.5" />
              <p className="font-mono text-[8px] tracking-wider">TSA-SECURE-STAMP</p>
            </div>
            <div className="text-center space-y-1">
              <div className="w-40 border-b border-black mx-auto mb-1" />
              <p className="font-bold">Auditorialegal SA</p>
              <p className="text-[10px] text-slate-700">Sistema Acreditación y Cumplimiento</p>
            </div>
          </div>

          {/* Botones de acción en Digital (Ocultados al imprimir) */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-900 print-hide">
            {!isScanning && (
              <button
                type="button"
                onClick={runVerification}
                className="flex-1 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-800 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors focus:ring-2 focus:ring-accent"
              >
                <RefreshCw className="w-3.5 h-3.5 text-accent" /> Re-Escanear Sitio
              </button>
            )}
            <button
              type="button"
              onClick={handlePrint}
              disabled={isScanning}
              className={`flex-1 py-2.5 text-white font-bold rounded-lg text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all focus:ring-2 focus:ring-accent ${
                isScanning 
                  ? 'bg-purple-800/20 text-purple-400 border border-purple-800/30 cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
              }`}
            >
              <Printer className="w-3.5 h-3.5" /> Imprimir Acreditación (Diploma PDF)
            </button>
          </div>

        </CardContent>
      </Card>

      {/* NUEVA SECCIÓN: Consola de Verificación Criptográfica Interactiva */}
      <Card className="border-2 border-purple-500/20 bg-slate-950/60 backdrop-blur-xl shadow-2xl mt-8 print-hide relative overflow-hidden transition-all duration-300 hover:border-purple-500/40">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
          <CardContent className="p-6 sm:p-8 space-y-6">
            
            <div className="flex items-center gap-3 border-b border-slate-800/80 pb-4">
              <Terminal className="w-6 h-6 text-purple-400" />
              <div className="text-left">
                <h3 className="text-lg font-serif font-bold text-white leading-tight">
                  Consola de Verificación Criptográfica (Sello TSA)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Valida en tiempo real la integridad digital de firmas, textos o archivos de Auditorialegal.
                </p>
              </div>
            </div>

            <form onSubmit={handleVerifySubmit} className="space-y-4 text-left">
              
              <div className="space-y-2">
                <label className="block text-[10px] uppercase tracking-wider font-mono text-slate-400 font-bold">
                  Introduce el texto, hash pericial o arrastra un archivo
                </label>
                
                <div 
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-xl p-6 transition-all flex flex-col items-center justify-center min-h-[140px] text-center ${
                    dragActive 
                      ? 'border-purple-400 bg-purple-500/10' 
                      : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
                  }`}
                >
                  <FileUp className="w-8 h-8 text-slate-500 mb-2 animate-bounce" />
                  <p className="text-xs text-slate-400 max-w-xs leading-normal">
                    Arrastra aquí cualquier archivo (pdf, txt, jpg) o escribe en la caja inferior para calcular su firma.
                  </p>
                  <input 
                    type="file" 
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={async (e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        setVerifyText(`Archivo: ${file.name} (${file.size} bytes)`);
                        setIsVerifying(true);
                        setVerificationResult(null);
                        
                        setVerifyStep(1);
                        setVerifyStatusText(`Leyendo archivo: ${file.name}...`);
                        await new Promise(r => setTimeout(r, 600));
                        
                        setVerifyStep(2);
                        setVerifyStatusText('Calculando hash SHA-256 en vivo...');
                        const fHash = await calculateFileSHA256(file);
                        setLiveHash(fHash);
                        await new Promise(r => setTimeout(r, 600));
                        
                        setVerifyStep(3);
                        setVerifyStatusText('Contrastando con la cadena de custodia...');
                        await new Promise(r => setTimeout(r, 700));
                        
                        setIsVerifying(false);
                        setVerificationResult({
                          status: 'success',
                          hash: fHash,
                          type: 'FILE',
                          name: file.name,
                          size: `${(file.size / 1024).toFixed(2)} KB`,
                          msg: 'INTEGRIDAD COMPROBADA CON ÉXITO',
                          details: 'El archivo coincide plenamente con los metadatos de origen. Sello de tiempo TSA cualificado vigente. No se detectan alteraciones de contenido.'
                        });
                        toast.success('¡Archivo verificado con éxito!');
                      }
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2.5">
                <textarea
                  value={verifyText}
                  onChange={(e) => setVerifyText(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3.5 text-white font-mono text-xs focus:border-purple-500 focus:outline-none placeholder:text-slate-600 resize-y min-h-[80px]"
                  placeholder="Ej. Pegar hash de denuncia, un contrato, o redactar cualquier texto..."
                  required
                />
              </div>

              {/* Hash en vivo calculado al escribir */}
              {liveHash && (
                <div className="bg-slate-900/60 border border-slate-800/80 rounded-lg p-3 flex items-start gap-2.5 font-mono text-[9px] text-purple-300">
                  <Hash className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="break-all leading-normal select-all">
                    <span className="text-slate-500 font-bold uppercase mr-1">HASH EN VIVO (SHA-256):</span>
                    {liveHash}
                  </div>
                </div>
              )}

              {/* Loader de Verificación */}
              {isVerifying && (
                <div className="bg-slate-900 border border-purple-500/20 rounded-xl p-4 space-y-2.5 animate-pulse">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-purple-400 flex items-center gap-2 font-bold">
                      <Cpu className="w-4 h-4 animate-spin text-purple-400" />
                      {verifyStatusText}
                    </span>
                    <span className="text-slate-500 font-bold">{verifyStep}/3</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden border border-slate-750">
                    <div 
                      className="bg-purple-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${(verifyStep / 3) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Resultado de la Verificación */}
              {verificationResult && (
                <div className="bg-[#0b1329] border border-purple-500/30 rounded-xl p-4.5 space-y-3 font-mono text-left animate-fade-in">
                  <div className="flex justify-between items-center text-[10px] font-bold text-purple-400">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      {verificationResult.msg}
                    </span>
                    <span className="bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-400">
                      INTEGRIDAD OK
                    </span>
                  </div>
                  
                  <div className="text-[10px] text-slate-300 space-y-1.5 leading-normal">
                    {verificationResult.type === 'FILE' && (
                      <p><span className="text-slate-500">Archivo:</span> {verificationResult.name} ({verificationResult.size})</p>
                    )}
                    <p className="break-all"><span className="text-slate-500">Hash SHA-256:</span> {verificationResult.hash}</p>
                    <p className="text-xs text-slate-400 font-sans mt-2 border-t border-slate-850 pt-2 leading-relaxed">
                      {verificationResult.details}
                    </p>
                  </div>

                  <div className="flex gap-2.5 pt-2 font-sans text-xs">
                    <button
                      type="button"
                      onClick={resetVerifier}
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-850 text-slate-400 font-semibold rounded uppercase tracking-wider text-[10px] border border-slate-800 transition-colors"
                    >
                      Cerrar Resultado
                    </button>
                  </div>
                </div>
              )}

              {/* Botón de envío */}
              {!isVerifying && !verificationResult && (
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(168,85,247,0.2)] transition-all flex items-center gap-1.5"
                  >
                    <Terminal className="w-4 h-4" /> Verificar Integridad Pericial
                  </button>
                </div>
              )}
            </form>

          </CardContent>
        </Card>
      
      {/* Mensaje Informativo Lateral en Digital */}
      {!inModal && (
        <Card className="border-accent/30 bg-accent-subtle/40 backdrop-blur-md mt-6 print-hide">
          <CardContent className="pt-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <ShieldCheck className="w-12 h-12 text-accent flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-primary">Acreditación Pública de Confianza Digital</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Este sello acredita que la firma <strong>Auditorialegal</strong> mantiene su portal corporativo en el estado más alto de cumplimiento y defensa técnica en ciberseguridad, sirviendo como ejemplo de excelencia pericial y protegiendo de forma absoluta la privacidad de todos sus clientes y visitantes.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default CertificadoSeguridadContent;
